import { useEffect, useState } from "react";
import { adminApi } from "@/lib/admin";
import { Loader2 } from "lucide-react";

// Admin-only cost tile for OpenAI Realtime (Direct Voice). We show:
//   1. Compact KPI cards at the top (minutes + $ this month).
//   2. A category breakdown row (audio in / audio out / text in /
//      text out) so a surprise-spend is instantly traceable to which
//      side of the call is expensive.
//   3. Top 5 consumers (per user) so we can spot a runaway account.
//   4. The last 20 sessions (details drill-down).
// Data comes from `/api/admin/realtime/usage` which computes cost
// server-side from the token counts posted by each finished session.
// Values are USD. Users never see this — the endpoint is admin-gated.

function fmtUSD(n) {
  const v = Number(n) || 0;
  if (v === 0) return "$0.00";
  if (v < 0.01) return `$${v.toFixed(4)}`;
  return v.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

function fmtMinutes(sec) {
  const m = (Number(sec) || 0) / 60;
  if (m >= 60) {
    const h = Math.floor(m / 60);
    const rest = Math.round(m - h * 60);
    return `${h}h ${rest}m`;
  }
  return `${m.toFixed(m >= 10 ? 0 : 1)} min`;
}

function fmtTokens(n) {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}k`;
  return v.toLocaleString();
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function KPI({ label, value, hint, testid }) {
  return (
    <div
      data-testid={testid}
      className="border border-[var(--border)] px-5 py-4"
    >
      <div className="font-mono-ui text-[9px] tracking-[0.28em] uppercase text-[var(--text-dim)]">
        {label}
      </div>
      <div className="font-serif text-3xl text-[var(--text)] mt-1">{value}</div>
      {hint && (
        <div className="font-mono-ui text-[10px] tracking-[0.12em] text-[var(--text-dim)] mt-1">
          {hint}
        </div>
      )}
    </div>
  );
}

function CategoryCard({ label, tokens, cached, cost, testid }) {
  return (
    <div
      data-testid={testid}
      className="border border-[var(--border)] px-4 py-3"
    >
      <div className="font-mono-ui text-[9px] tracking-[0.28em] uppercase text-[var(--text-dim)]">
        {label}
      </div>
      <div className="flex items-baseline justify-between mt-2 gap-3">
        <span className="font-serif text-xl text-[var(--text)]">{fmtUSD(cost)}</span>
        <span className="font-mono-ui text-[10px] text-[var(--text-dim)]">
          {fmtTokens(tokens)} tok
        </span>
      </div>
      {typeof cached === "number" && cached > 0 && (
        <div className="font-mono-ui text-[10px] text-[var(--text-dim)] mt-1">
          cached · {fmtTokens(cached)}
        </div>
      )}
    </div>
  );
}

export default function RealtimeUsageSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await adminApi.realtimeUsage();
      setData(res);
    } catch (e) {
      setError(e?.message || "Failed to load Realtime usage.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (loading && !data) {
    return (
      <div
        data-testid="realtime-usage-loading"
        className="flex items-center gap-2 font-sans text-sm text-[var(--text-dim)]"
      >
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading Realtime usage…
      </div>
    );
  }

  if (error && !data) {
    return (
      <div
        data-testid="realtime-usage-error"
        className="font-sans text-sm text-[var(--text-muted)]"
      >
        {error}
      </div>
    );
  }

  const totals = data?.totals || {};
  const split = data?.split || {};
  const top = data?.top || [];
  const recent = data?.recent || [];
  const monthLabel = data?.month_start
    ? new Date(data.month_start).toLocaleDateString(undefined, {
        month: "long",
        year: "numeric",
      })
    : "this month";

  return (
    <div data-testid="realtime-usage-section" className="space-y-6">
      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI
          label="Minutes used"
          value={fmtMinutes(totals.duration_sec || 0)}
          hint={monthLabel}
          testid="realtime-kpi-minutes"
        />
        <KPI
          label="Spend"
          value={fmtUSD(totals.cost_usd || 0)}
          hint={monthLabel}
          testid="realtime-kpi-cost"
        />
        <KPI
          label="Sessions"
          value={(totals.sessions || 0).toLocaleString()}
          hint={monthLabel}
          testid="realtime-kpi-sessions"
        />
        <KPI
          label="Avg call"
          value={
            totals.sessions
              ? fmtMinutes((totals.duration_sec || 0) / totals.sessions)
              : "—"
          }
          hint="per session"
          testid="realtime-kpi-avg"
        />
      </div>

      {/* Split by category */}
      <div>
        <div className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-[var(--text-dim)] mb-2">
          Cost breakdown
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <CategoryCard
            label="Audio in"
            tokens={split.audio_input?.tokens}
            cached={split.audio_input?.cached_tokens}
            cost={split.audio_input?.cost_usd}
            testid="realtime-split-audio-in"
          />
          <CategoryCard
            label="Audio out"
            tokens={split.audio_output?.tokens}
            cost={split.audio_output?.cost_usd}
            testid="realtime-split-audio-out"
          />
          <CategoryCard
            label="Text in"
            tokens={split.text_input?.tokens}
            cached={split.text_input?.cached_tokens}
            cost={split.text_input?.cost_usd}
            testid="realtime-split-text-in"
          />
          <CategoryCard
            label="Text out"
            tokens={split.text_output?.tokens}
            cost={split.text_output?.cost_usd}
            testid="realtime-split-text-out"
          />
        </div>
      </div>

      {/* Top consumers */}
      <div>
        <div className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-[var(--text-dim)] mb-2">
          Top consumers · {monthLabel}
        </div>
        {top.length === 0 ? (
          <p
            data-testid="realtime-top-empty"
            className="font-sans text-sm text-[var(--text-dim)]"
          >
            No Realtime sessions yet this month.
          </p>
        ) : (
          <div className="border border-[var(--border)] divide-y divide-[var(--border)]">
            {top.map((u, idx) => (
              <div
                key={u.user_id || idx}
                data-testid={`realtime-top-row-${idx}`}
                className="grid grid-cols-12 gap-3 px-4 py-2.5 items-baseline"
              >
                <span className="col-span-1 font-mono-ui text-[10px] text-[var(--text-dim)]">
                  {String(idx + 1).padStart(2, "0")}
                </span>
                <span className="col-span-6 font-sans text-sm text-[var(--text)] truncate">
                  {u.user_email}
                </span>
                <span className="col-span-2 font-mono-ui text-[11px] text-[var(--text-muted)] text-right">
                  {u.sessions}× · {fmtMinutes(u.duration_sec)}
                </span>
                <span className="col-span-3 font-serif text-base text-[var(--text)] text-right">
                  {fmtUSD(u.cost_usd)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent sessions */}
      <div>
        <div className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-[var(--text-dim)] mb-2">
          Recent sessions
        </div>
        {recent.length === 0 ? (
          <p
            data-testid="realtime-recent-empty"
            className="font-sans text-sm text-[var(--text-dim)]"
          >
            No completed sessions yet.
          </p>
        ) : (
          <div className="border border-[var(--border)] divide-y divide-[var(--border)] max-h-96 overflow-auto">
            {recent.map((s, idx) => (
              <div
                key={s.session_id || idx}
                data-testid={`realtime-recent-row-${idx}`}
                className="grid grid-cols-12 gap-3 px-4 py-2 items-baseline"
              >
                <span className="col-span-3 font-mono-ui text-[11px] text-[var(--text-dim)]">
                  {fmtDate(s.started_at)}
                </span>
                <span className="col-span-5 font-sans text-sm text-[var(--text)] truncate">
                  {s.user_email}
                </span>
                <span className="col-span-2 font-mono-ui text-[11px] text-[var(--text-muted)] text-right">
                  {fmtMinutes(s.duration_sec)}
                </span>
                <span className="col-span-2 font-serif text-sm text-[var(--text)] text-right">
                  {fmtUSD(s.cost_usd)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Refresh */}
      <div className="flex items-center justify-between pt-2">
        <div className="font-mono-ui text-[10px] text-[var(--text-dim)]">
          Pricing: audio in {fmtUSD(data?.pricing_usd_per_million?.audio_input)} / audio out{" "}
          {fmtUSD(data?.pricing_usd_per_million?.audio_output)} / audio-cached{" "}
          {fmtUSD(data?.pricing_usd_per_million?.audio_input_cached)} per 1M tok.
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          data-testid="realtime-refresh-btn"
          className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--text-muted)] hover:text-[var(--text)] transition-colors disabled:opacity-50"
        >
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>
    </div>
  );
}
